import PropTypes from 'prop-types';
import { get, set, cloneDeep } from 'lodash';
import React, { PureComponent } from 'react';
import { Button, Form, Grid, Row, Col } from 'react-bootstrap/lib';
import { getDealerFlow } from '@upstart/harmony/lib/flow-utils';
import {
  CLIENT_TYPE,
  OnlineStepPath,
  OnlineStep,
} from '@upstart/harmony/lib/constants';
import Switch from '../inputs/Switch';
import TextInput from '../inputs/TextInput';
import NumberInput from '../inputs/NumberInput';
import ColorPicker from '../inputs/ColorPicker';
import CodeTextArea from '../inputs/CodeTextArea';
import ImageUpload from '../inputs/ImageUpload';
import Select from '../inputs/Select';
import { widgetsScriptURL } from '../../../util/constants';

class PricingButtonGroupEditor extends PureComponent {
  constructor(props) {
    super(props);
    this.opts = getDealerFlow(this.props.dealer)
      [CLIENT_TYPE.ONLINE].filter(
        (step) =>
          // Filter out invalid steps
          // (not valid target pages for CTA widget button)
          [OnlineStep.upgradePackages, OnlineStep.schedulePickup].indexOf(
            step.id,
          ) === -1,
      )
      .map((activeStep) => ({
        id: OnlineStepPath[activeStep.id],
        name: activeStep.name,
      }));

    // Always add Vehicle Details page as first option
    this.opts.unshift({
      id: 'start',
      name: 'Vehicle Detail',
    });
  }

  onChange = (e) => {
    const formData = cloneDeep(this.props.formData);
    set(formData, e.target.name, e.target.value);
    this.props.onChange(formData);
  };

  handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();

    this.props.onSave();
  };

  // We cannot turn on widget rebates until APR purchase rebates are enabled
  areIncentivesDisabled = () => {
    const rebateStatus = get(
      this.props.dealer,
      'aprConfig.options.useManufacturerRebate.purchase',
    );
    // If rebates are off, this cannot go on until those come on.
    if (rebateStatus === false) {
      return true;
    }
    return false;
  };

  renderActionButtonSection = ({ name, buttonType, placeholder }) => {
    const { formData } = this.props;
    const buttonEnabled = get(formData, `${buttonType}.enabled`);
    return (
      <div className="widget-text-toggle-group">
        <Switch
          name={`${buttonType}.enabled`}
          label={`${name} Button`}
          formData={formData}
          onChange={this.onChange}
        />
        {buttonEnabled && (
          <>
            <TextInput
              name={`${buttonType}.text`}
              label={`${name} Button Text`}
              formData={formData}
              onChange={this.onChange}
              placeholder={placeholder}
            />
            {get(formData, 'useCTAImages') && (
              <ImageUpload
                name={`${buttonType}.image`}
                label={`${name} Image Upload`}
                formData={formData}
                onChange={this.onChange}
              />
            )}
          </>
        )}
      </div>
    );
  };

  render() {
    const {
      dealer,
      formData,
      page: { type, title },
    } = this.props;
    return (
      <Form onSubmit={this.handleSubmit}>
        <Grid fluid>
          <Row>
            <Col sm={12}>
              <h3>{title}</h3>
            </Col>
          </Row>
          <Row>
            <Col sm={5}>
              <h4>Display Options</h4>
              <div className="widget-text-toggle-group">
                <Switch
                  name="msrp.enabled"
                  label="MSRP"
                  formData={formData}
                  onChange={this.onChange}
                />
                <div>
                  <Switch
                    name="msrp.visible"
                    label="Price Visibility"
                    formData={formData}
                    onChange={this.onChange}
                    disabled={!get(formData, 'msrp.enabled')}
                  />
                </div>
                <div>
                  <Switch
                    name="discounts.enabled"
                    label="Dealer Discount"
                    formData={formData}
                    onChange={this.onChange}
                  />
                </div>
                <div>
                  <TextInput
                    name="discounts.text"
                    label="Dealer Discount Label"
                    formData={formData}
                    onChange={this.onChange}
                    placeholder="Discount"
                    disabled={!get(formData, 'discounts.enabled')}
                  />
                </div>
                {this.areIncentivesDisabled() && (
                  <span className="widget-warning">
                    &quot;Incentives & Rebates&quot; is disabled until Pricing
                    &gt;&gt; Online APR Configuration &gt;&gt; Manufacturer
                    Rebates &gt;&gt; Purchase is Enabled
                  </span>
                )}
                <div>
                  <Switch
                    name="incentives.enabled"
                    label="Incentives & Rebates"
                    formData={formData}
                    onChange={this.onChange}
                    disabled={this.areIncentivesDisabled()}
                  />
                </div>
              </div>
              <div className="widget-text-toggle-group">
                <Switch
                  name="bestPrice.enabled"
                  label="Best Price"
                  formData={formData}
                  onChange={this.onChange}
                />
                {get(formData, 'bestPrice.enabled') && (
                  <div>
                    <TextInput
                      name="bestPrice.text"
                      label="Best Price Text"
                      formData={formData}
                      onChange={this.onChange}
                      placeholder="Our Price"
                    />
                    <Switch
                      name="bestPrice.visible"
                      label="Price Visibility"
                      formData={formData}
                      onChange={this.onChange}
                    />
                  </div>
                )}
              </div>
              <div className="widget-text-toggle-group">
                <Switch
                  name="lowestPrice.enabled"
                  label="Lowest Price"
                  formData={formData}
                  onChange={this.onChange}
                />
                {get(formData, 'lowestPrice.enabled') && (
                  <div>
                    <TextInput
                      name="lowestPrice.text"
                      label="Lowest Price Text"
                      formData={formData}
                      onChange={this.onChange}
                      placeholder="Lowest Price"
                    />
                    <TextInput
                      name="lowestPrice.disclaimer"
                      label="Lowest Price Disclaimer"
                      formData={formData}
                      onChange={this.onChange}
                      maxLength="300"
                      placeholder="Includes incentives available to all consumers and, in some cases, conditional incentives available to most consumers. In addition, other incentives may be available. Incentives are offered on a limited time basis. Please see dealer for full details."
                    />
                  </div>
                )}
              </div>
              <div className="widget-text-toggle-group">
                <Switch
                  name="enableDisclaimerText"
                  label="Enable Disclaimer Text"
                  formData={formData}
                  onChange={this.onChange}
                />
                {get(formData, 'enableDisclaimerText') && (
                  <TextInput
                    name="disclaimerText"
                    label="Disclaimer Text"
                    formData={formData}
                    onChange={this.onChange}
                    placeholder="*Includes OEM incentives and rebates"
                    required={false}
                  />
                )}
              </div>
              <div className="widget-text-toggle-group">
                <Switch
                  name="useCTAImages"
                  label="CTA Images Instead of Text"
                  formData={formData}
                  onChange={this.onChange}
                />
              </div>
              <h4>Lead Capture Button</h4>
              <Select
                label="Target Page"
                name="leadCaptureButton.target"
                value={formData.leadCaptureButton.target}
                formData={formData}
                onChange={this.onChange}
                info="Page that user will land on after clicking this button"
              >
                {this.opts.map((s) => (
                  <Select.Option name={s.name} value={s.id} />
                ))}
              </Select>
              {get(formData, 'useCTAImages') && (
                <>
                  <ImageUpload
                    name="leadCaptureButton.image"
                    label="Button Image - New Vehicle"
                    formData={formData}
                    onChange={this.onChange}
                  />
                  <ImageUpload
                    name="leadCaptureButton.dealImage"
                    label="Button Image - New Vehicle With Deal"
                    formData={formData}
                    onChange={this.onChange}
                  />
                  <ImageUpload
                    name="leadCaptureButton.imageUsed"
                    label="Button Image - Used Vehicle"
                    formData={formData}
                    onChange={this.onChange}
                  />
                  <ImageUpload
                    name="leadCaptureButton.dealImageUsed"
                    label="Button Image - Used Vehicle With Deal"
                    formData={formData}
                    onChange={this.onChange}
                  />
                </>
              )}

              <TextInput
                name="leadCaptureButton.text"
                label="Button Text"
                formData={formData}
                onChange={this.onChange}
                placeholder={get(formData, 'leadCaptureButton.defaultText')}
              />
              <TextInput
                name="leadCaptureButton.dealText"
                label="Button Text with Deal"
                formData={formData}
                onChange={this.onChange}
                placeholder={get(formData, 'leadCaptureButton.defaultDealText')}
              />
              <NumberInput
                name="leadCaptureButton.textSize"
                label="Button Text Size"
                formData={formData}
                onChange={this.onChange}
                info="Enter value in pixels"
              />
              <NumberInput
                name="leadCaptureButton.radius"
                label="Button Radius"
                formData={formData}
                onChange={this.onChange}
                info="Enter value in pixels"
                min={0}
                max={25}
                isRequired={false}
              />
              <ColorPicker
                name="leadCaptureButton.backgroundColor"
                label="Button Background Color"
                formData={formData}
                onChange={this.onChange}
              />
              <ColorPicker
                name="leadCaptureButton.textColor"
                label="Button Text Color"
                formData={formData}
                onChange={this.onChange}
              />
            </Col>
            <Col sm={5} smOffset={1}>
              <h4>Action Buttons</h4>
              <NumberInput
                name="actionTextSize"
                label="Button Text Size"
                formData={formData}
                onChange={this.onChange}
                info="Enter value in pixels"
              />
              <NumberInput
                name="actionRadius"
                label="Button Radius"
                formData={formData}
                onChange={this.onChange}
                info="Enter value in pixels"
                min={0}
                max={25}
                isRequired={false}
              />
              <ColorPicker
                name="actionBackgroundColor"
                label="Button Background Color"
                formData={formData}
                onChange={this.onChange}
              />
              <ColorPicker
                name="actionTextColor"
                label="Button Text Color"
                formData={formData}
                onChange={this.onChange}
              />
              <NumberInput
                name="actionButtonMargin"
                label="Button Margin"
                formData={formData}
                onChange={this.onChange}
                info="Enter value in pixels"
                min={10}
                max={50}
                isRequired={false}
              />
              {this.renderActionButtonSection({
                name: 'TradeIn',
                buttonType: 'tradeInButton',
                placeholder: 'Value Your Trade',
              })}
              {this.renderActionButtonSection({
                name: 'Payment Options',
                buttonType: 'paymentOptionsButton',
                placeholder: 'Customize Your Payment Options',
              })}
              {this.renderActionButtonSection({
                name: 'Pre-Approved',
                buttonType: 'preApprovalButton',
                placeholder: 'Get Pre-Approved',
              })}
              {this.renderActionButtonSection({
                name: 'Test Drive',
                buttonType: 'testDriveButton',
                placeholder: 'Schedule Your Test Drive',
              })}
            </Col>
          </Row>
          <Row>
            <Col sm={12}>
              <h4>Markup</h4>
              <small>
                Copy the markup below into your page template to render the
                Button Widget. Only add this script once per page.
              </small>
              <CodeTextArea
                label="Agent Script"
                value={`<!— Copy this markup into your page template after the closing <body/> tag —>\n<script src="${widgetsScriptURL}" type="text/javascript" id="prodigyButtonGroupAgent" dealerId="${dealer.id}"></script>`}
              />
              <CodeTextArea
                label="Button Container"
                value={`<!— Copy this markup into your page template where you want the button group to render —>\n<div class="prodigy-pricing-button-group" data-vin="" data-config-type="${type}"></div>`}
              />
            </Col>
          </Row>
          <Row>
            <Col sm={12}>
              <Button bsClass="btn btn-primary" type="submit">
                Preview
              </Button>
            </Col>
          </Row>
        </Grid>
      </Form>
    );
  }
}

PricingButtonGroupEditor.propTypes = {
  dealer: PropTypes.object,
  page: PropTypes.object,
  formData: PropTypes.object,
  onChange: PropTypes.func,
  onSave: PropTypes.func,
};

export default PricingButtonGroupEditor;
